import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { TOOL_GROUPS } from "../routes";

export default function FreeDeveloperTools({ t }) {
  const css = `
.fdt-page { max-width: 860px; margin: 0 auto; padding: 3rem 2rem 5rem; flex: 1; }
.fdt-hero { margin-bottom: 3.5rem; }
.fdt-eyebrow { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.14em; color: ${t.textMuted}; font-family: 'JetBrains Mono', monospace; margin-bottom: 10px; }
.fdt-h1 { font-size: clamp(28px, 4.5vw, 46px); font-weight: 800; letter-spacing: -0.03em; color: ${t.textPrimary}; margin-bottom: 16px; line-height: 1.1; }
.fdt-h1 em { font-style: normal; background: linear-gradient(135deg, #F05A41 0%, #D8A056 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
.fdt-lead { font-size: 16px; color: ${t.textSecondary}; line-height: 1.7; max-width: 640px; margin-bottom: 24px; }
.fdt-badges { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 2rem; }
.fdt-badge { display: inline-flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: 20px; font-size: 12px; font-weight: 700; font-family: 'JetBrains Mono', monospace; border: 1px solid rgba(240,90,65,0.25); background: rgba(240,90,65,0.07); color: ${t.textAccent}; }
.fdt-section { margin-bottom: 3rem; }
.fdt-section-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.14em; color: ${t.textMuted}; font-family: 'JetBrains Mono', monospace; margin-bottom: 6px; }
.fdt-section-title { font-size: 22px; font-weight: 800; color: ${t.textPrimary}; letter-spacing: -0.03em; margin-bottom: 12px; }
.fdt-section-desc { font-size: 14px; color: ${t.textSecondary}; line-height: 1.65; margin-bottom: 18px; max-width: 600px; }
.fdt-tool-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 10px; }
.fdt-tool-card { display: flex; align-items: flex-start; gap: 14px; background: ${t.bgPanel}; border: 1px solid ${t.border}; border-radius: 12px; padding: 16px 18px; text-decoration: none; transition: border-color 0.15s, transform 0.15s; }
.fdt-tool-card:hover { border-color: rgba(240,90,65,0.35); transform: translateY(-2px); }
.fdt-tool-icon { width: 38px; height: 38px; border-radius: 10px; background: linear-gradient(135deg, rgba(240,90,65,0.12), rgba(216,160,86,0.12)); display: flex; align-items: center; justify-content: center; font-size: 17px; flex-shrink: 0; }
.fdt-tool-name { font-size: 14px; font-weight: 700; color: ${t.textPrimary}; margin-bottom: 4px; }
.fdt-tool-desc { font-size: 12px; color: ${t.textMuted}; font-family: 'JetBrains Mono', monospace; line-height: 1.5; }
.fdt-divider { height: 1px; background: ${t.border}; margin: 3rem 0; }
.fdt-faq { margin-bottom: 3rem; }
.fdt-faq-item { border: 1px solid ${t.border}; border-radius: 12px; margin-bottom: 10px; overflow: hidden; }
.fdt-faq-item summary { padding: 16px 20px; font-size: 14px; font-weight: 700; color: ${t.textPrimary}; cursor: pointer; list-style: none; display: flex; align-items: center; justify-content: space-between; }
.fdt-faq-item summary::after { content: '+'; font-size: 20px; color: ${t.textAccent}; font-weight: 400; flex-shrink: 0; }
.fdt-faq-item[open] summary::after { content: '−'; }
.fdt-faq-body { padding: 0 20px 16px; font-size: 13px; color: ${t.textSecondary}; line-height: 1.75; }
.fdt-cta { background: linear-gradient(135deg, rgba(240,90,65,0.08) 0%, rgba(216,160,86,0.08) 100%); border: 1px solid rgba(240,90,65,0.20); border-radius: 16px; padding: 32px 28px; text-align: center; }
.fdt-cta-title { font-size: 22px; font-weight: 800; color: ${t.textPrimary}; margin-bottom: 10px; letter-spacing: -0.03em; }
.fdt-cta-desc { font-size: 14px; color: ${t.textSecondary}; margin-bottom: 20px; }
.fdt-footer-nav { display: flex; gap: 20px; flex-wrap: wrap; padding-top: 28px; border-top: 1px solid ${t.border}; margin-top: 3rem; }
.fdt-footer-nav a { font-size: 13px; color: ${t.textAccent}; text-decoration: none; font-weight: 600; }
@media (max-width: 600px) { .fdt-page { padding: 2rem 1.25rem 4rem; } .fdt-tool-grid { grid-template-columns: 1fr; } }
`;

  const CATEGORY_DESCS = {
    "JSON": "A complete JSON toolkit for formatting, validating, converting, and comparing JSON data. Perfect for API debugging, configuration file editing, and data pipeline work.",
    "Encode / Decode": "14 encoding and decoding tools for everyday developer tasks: Base64, URL, HTML entities, JWT tokens, Hex, Unicode, Morse code, and quick hashing.",
    "Generators": "Generate UUIDs, secure passwords, Lorem Ipsum placeholder text, and QR codes. All generation happens locally — nothing is sent to any server.",
    "Security": "Compute MD5, SHA-256, and SHA-512 hashes for text and files. File hashing runs via the Web Crypto API — your files never leave the browser.",
    "Convert": "Convert between PDF, Word (DOCX), PNG images, and more. Every conversion runs in your browser using JavaScript — no server required.",
    "Time & Date": "Unix timestamp converter, timezone converter for 35+ zones, and a visual cron expression builder with plain-English descriptions and next-run previews.",
    "Text": "Text diff checker, duplicate line remover, 12-format case converter, and a live regex tester with 10 built-in presets. No upload, no tracking.",
    "Compress": "Compress images (JPG/PNG/WebP), PDFs, create ZIP archives, optimize SVGs, and minify CSS, HTML, and JSON — all in your browser.",
  };

  const FAQ_ITEMS = [
    {
      q: "Are all the tools on miniDevToolkit really free?",
      a: "Yes. Every tool is completely free with no subscription, no hidden fees, no account required, and no usage limits. The site is supported by non-intrusive ads.",
    },
    {
      q: "Do I need to create an account to use any tool?",
      a: "No. All tools work instantly without signing up or providing an email address. Just open the site and start using any tool.",
    },
    {
      q: "Does miniDevToolkit upload my files or data to a server?",
      a: "Never. Every tool runs entirely client-side using JavaScript in your browser tab. Your JSON, files, passwords, text, and all other inputs are processed locally on your device and never transmitted to any server. You can verify this by checking the Network tab in browser DevTools.",
    },
    {
      q: "Does miniDevToolkit work offline?",
      a: "Yes. After the first page load, all tools work without an internet connection. The only exception is tools that lazy-load libraries from CDNs (like the PDF converter) — those require an internet connection on first use.",
    },
    {
      q: "How is miniDevToolkit different from CyberChef or JSONFormatter.org?",
      a: "miniDevToolkit bundles tools from multiple categories into a single consistent interface. Unlike CyberChef (which is powerful but complex), miniDevToolkit focuses on simplicity and everyday developer tasks. Unlike JSONFormatter.org (JSON-only), miniDevToolkit covers JSON, encoding, hashing, generators, file conversion, time tools, text utilities, and compression. All tools share the same strict client-side privacy guarantee.",
    },
    {
      q: "Can I use miniDevToolkit on mobile?",
      a: "Yes. The site is fully responsive and works on all screen sizes from 320px upward. The sidebar collapses to an overlay on mobile.",
    },
    {
      q: "How many tools are included?",
      a: "miniDevToolkit includes 25+ individual tools across 9 toolkits: JSON (7 tools), Encoding (14 tools), Generators (4 tools), Fake Data Generator, Hash & Security (4 tools), File Converter (4 modes), Time & Date (3 tools), Text Utilities (4 tools), and Compression Toolkit (5 tools).",
    },
    {
      q: "Is there a dark mode?",
      a: "Yes. miniDevToolkit supports both dark mode (default) and light mode. Toggle between them using the theme button in the top-right corner. Your preference is saved in localStorage.",
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ_ITEMS.map(item => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": { "@type": "Answer", "text": item.a },
    })),
  };

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "25+ Free Online Developer Tools — No Sign-Up | miniDevToolkit",
    "url": "https://minidevtoolkit.com/free-developer-tools",
    "description": "Browse 25+ free browser-based developer tools. JSON formatter, Base64 encoder, UUID generator, hash generator, regex tester, file converter, cron builder and more. No sign-up, no uploads, 100% client-side.",
    "publisher": { "@type": "Organization", "name": "miniDevToolkit", "url": "https://minidevtoolkit.com/" },
  };

  return (
    <>
      <Helmet>
        <title>25+ Free Online Developer Tools — No Sign-Up | miniDevToolkit</title>
        <meta name="description" content="Browse 25+ free browser-based developer tools. JSON formatter, Base64 encoder, UUID generator, hash generator, regex tester, file converter, cron builder and more. No sign-up, no uploads, 100% client-side." />
        <link rel="canonical" href="https://minidevtoolkit.com/free-developer-tools" />
        <meta property="og:title" content="25+ Free Online Developer Tools — No Sign-Up | miniDevToolkit" />
        <meta property="og:description" content="Browse 25+ free browser-based developer tools. JSON formatter, Base64 encoder, UUID generator, hash generator, regex tester, file converter, cron builder and more. No sign-up, no uploads, 100% client-side." />
        <meta property="og:url" content="https://minidevtoolkit.com/free-developer-tools" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <style>{css}</style>

      <main className="fdt-page">
        <div className="fdt-hero">
          <div className="fdt-eyebrow">Free Developer Tools</div>
          <h1 className="fdt-h1">
            25+ Free Developer Tools That<br />
            <em>Run Entirely in Your Browser.</em>
          </h1>
          <p className="fdt-lead">
            Every tool on miniDevToolkit runs 100% client-side — your data never leaves your device.
            No sign-up, no uploads, no server, no tracking. Just open and use.
          </p>
          <div className="fdt-badges">
            <span className="fdt-badge">🔒 100% Private</span>
            <span className="fdt-badge">🚫 No Sign-Up</span>
            <span className="fdt-badge">⚡ Works Offline</span>
            <span className="fdt-badge">✓ Completely Free</span>
          </div>
        </div>

        {TOOL_GROUPS.map(grp => (
          <section key={grp.group} className="fdt-section" aria-labelledby={`grp-${grp.group}`}>
            <div className="fdt-section-label">{grp.group}</div>
            <h2 className="fdt-section-title" id={`grp-${grp.group}`}>{grp.group} Tools</h2>
            <p className="fdt-section-desc">{CATEGORY_DESCS[grp.group] || ""}</p>
            <div className="fdt-tool-grid">
              {grp.tools.map(tool => (
                <Link key={tool.id} to={tool.path} className="fdt-tool-card" aria-label={`Open ${tool.name}`}>
                  <div className="fdt-tool-icon">{tool.icon}</div>
                  <div>
                    <div className="fdt-tool-name">{tool.name}</div>
                    <div className="fdt-tool-desc">{tool.desc}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}

        <div className="fdt-divider" />

        <section className="fdt-faq" aria-labelledby="fdt-faq-heading">
          <div className="fdt-section-label">FAQ</div>
          <h2 className="fdt-section-title" id="fdt-faq-heading">Frequently Asked Questions</h2>
          {FAQ_ITEMS.map((item, i) => (
            <details key={i} className="fdt-faq-item">
              <summary>{item.q}</summary>
              <div className="fdt-faq-body">{item.a}</div>
            </details>
          ))}
        </section>

        <div className="fdt-cta">
          <div className="fdt-cta-title">Ready to try a tool?</div>
          <div className="fdt-cta-desc">All tools are free, private, and open instantly — no account needed.</div>
          <Link to="/" className="btn-primary" style={{ display: "inline-block", padding: "12px 28px", fontSize: 14, textDecoration: "none" }}>
            Browse all tools →
          </Link>
        </div>

        <nav className="fdt-footer-nav" aria-label="Related pages">
          <Link to="/">← Home</Link>
          <Link to="/faq">FAQ</Link>
          <Link to="/alternatives/jsonformatter">vs JSONFormatter</Link>
          <Link to="/alternatives/cyberchef">vs CyberChef</Link>
          <Link to="/blog/best-free-dev-tools-2026">Best Dev Tools 2026</Link>
        </nav>
      </main>
    </>
  );
}
